import React from 'react';
import {
  Card as MuiCard,
  CardContent,
  CardHeader,
  CardActions,
  Divider,
  IconButton,
  Skeleton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import MoreVertIcon from '@mui/icons-material/MoreVert';

const StyledCard = styled(MuiCard)(({ theme, variant }) => ({
  borderRadius: theme.spacing(1.5),
  boxShadow: theme.shadows[1],
  transition: 'all 0.2s ease-in-out',

  '&:hover': {
    boxShadow: theme.shadows[4],
    transform: 'translateY(-2px)',
  },

  // Custom variants
  ...(variant === 'outlined' && {
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: 'none',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      boxShadow: theme.shadows[2],
    },
  }),

  ...(variant === 'elevated' && {
    boxShadow: theme.shadows[8],
    '&:hover': {
      boxShadow: theme.shadows[12],
    },
  }),

  ...(variant === 'gradient' && {
    background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}15 100%)`,
    backdropFilter: 'blur(10px)',
  }),
}));

/**
 * Standardized Card component with consistent styling and features
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Card content
 * @param {string} props.title - Card title
 * @param {string} props.subtitle - Card subtitle
 * @param {React.ReactNode} props.avatar - Avatar component
 * @param {React.ReactNode} props.action - Action component (usually IconButton)
 * @param {React.ReactNode} props.actions - Card actions (buttons, etc.)
 * @param {boolean} props.loading - Show loading skeleton
 * @param {string} props.variant - Card styling variant
 * @param {boolean} props.divider - Show divider between header and content
 * @param {boolean} props.clickable - Make card clickable
 * @param {function} props.onClick - Click handler
 * @param {number} props.elevation - Card elevation (0-24)
 */
const Card = ({
  children,
  title,
  subtitle,
  avatar,
  action,
  actions,
  loading = false,
  variant = 'elevated',
  divider = false,
  clickable = false,
  onClick,
  elevation,
  ...otherProps
}) => {
  const hasHeader = title || subtitle || avatar || action;

  const handleClick = (event) => {
    if (clickable && onClick) {
      onClick(event);
    }
  };

  if (loading) {
    return (
      <StyledCard variant={variant} elevation={elevation} {...otherProps}>
        {hasHeader && (
          <CardHeader
            avatar={<Skeleton variant="circular" width={40} height={40} />}
            title={<Skeleton variant="text" width="60%" />}
            subheader={<Skeleton variant="text" width="40%" />}
            action={<Skeleton variant="circular" width={24} height={24} />}
          />
        )}
        {divider && <Divider />}
        <CardContent>
          <Skeleton variant="text" />
          <Skeleton variant="text" />
          <Skeleton variant="rectangular" height={100} sx={{ mt: 1 }} />
        </CardContent>
        {actions && (
          <CardActions>
            <Skeleton variant="rectangular" width={80} height={32} />
            <Skeleton variant="rectangular" width={80} height={32} />
          </CardActions>
        )}
      </StyledCard>
    );
  }

  return (
    <StyledCard
      variant={variant}
      elevation={elevation}
      onClick={handleClick}
      sx={{
        cursor: clickable ? 'pointer' : 'default',
        ...(clickable && {
          '&:hover': {
            transform: 'translateY(-4px)',
          },
        }),
      }}
      {...otherProps}
    >
      {hasHeader && (
        <CardHeader
          avatar={avatar}
          title={title}
          subheader={subtitle}
          action={
            action ||
            (title && (
              <IconButton>
                <MoreVertIcon />
              </IconButton>
            ))
          }
        />
      )}

      {divider && <Divider />}

      <CardContent>{children}</CardContent>

      {actions && (
        <>
          <Divider />
          <CardActions>{actions}</CardActions>
        </>
      )}
    </StyledCard>
  );
};

Card.propTypes = {
  children: PropTypes.node,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  avatar: PropTypes.node,
  action: PropTypes.node,
  actions: PropTypes.node,
  loading: PropTypes.bool,
  variant: PropTypes.oneOf(['elevated', 'outlined', 'gradient']),
  divider: PropTypes.bool,
  clickable: PropTypes.bool,
  onClick: PropTypes.func,
  elevation: PropTypes.number,
};

export default Card;
